/* eslint-disable no-underscore-dangle */
import * as R from "ramda";
import {
  put,
  take,
  all,
  takeEvery,
  fork,
  select,
  call
} from "redux-saga/effects";
import { channel } from "redux-saga";
import { actions, getters } from "Portal/ApplicationForm/model";
import {
  getContactValues,
  getContactDataFields
} from "Portal/ApplicationForm/selectors";
import { getCredentials } from "redux/modules/user/selectors";
import {
  userId as getUserId,
  user as getUser
} from "redux/modules/user/selectors";
import Helpers from "utils/Global/Helpers";

import { registerError } from "redux/modules/errors/actions";
import Api from "Portal/ApplicationForm/api";
import PortalUsersApi from "redux/modules/portal/users/api";
import UserApi from "redux/modules/user/api";
import { actions as TabActions } from "ui-kit/Tabs";
import SSOApi from "redux/modules/sso/api";

const getParams = function*() {
  const formData = yield select(getters.formData);
  const intakeId = yield select(getters.intakeId);
  const userId = yield select(getUserId);

  return {
    userId,
    eventId: formData.event_id,
    eventUUID: formData.event_uuid,
    intakeId
  };
};

const loginChannel = channel();

const init = function*({
  payload: { formId, eventSlug, submissionId, postLogin }
}) {
  const credentials = yield select(getCredentials);
  const userId = yield select(getUserId);
  const user = yield select(getUser);
  yield put(actions.setLoading(true));
  try {
    yield put(actions.setIntakeId(formId));

    const { payload } = yield call(Api.getForm, {
      credentials,
      formId,
      query: {
        submissionId
      }
    });

    const userEmail = user?.email ?? null;

    if (submissionId && !userId) {
      window.location = `/login?returnTo=/applications/${eventSlug}/${formId}/submissions/${submissionId}`;
      return false;
    }

    yield put(
      actions.seedFormData({
        submissionId,
        userEmail,
        formPayload: payload,
        isLoggedIn: Boolean(userId),
        loggedInLabel:
          user.fname && user.lname.length
            ? `${user.fname} ${user.lname} (${user.email})`
            : user.email,
        selectedContactId: null,
        contactValues: payload.contact_values
      })
    );

    if (postLogin) {
      yield put(actions.setActiveIndex(1));
    }

    /*
    if (
      contactId &&
      payload.selected_contact_values &&
      payload.selected_contact_values[STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]
    ) {
      yield put(
        actions.checkUser({
          id: STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
          value:
            payload.selected_contact_values[
              STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
            ]
        })
      );
    }
    */

    yield put(actions.fetchSingleSignOnIdentityProviders());
  } catch (e) {
    yield all([
      put(
        registerError([
          {
            system: e,
            user: "An error occurred getting intake form data"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setLoading(false));
  }
  return true;
};

const handleCallback = function*({ payload }) {
  window.location = `/applications/${payload.slug}/${payload.intakeFormId}?postLogin=1`;
  return true;

  // yield call(init, {
  //   payload: { eventUUID: payload.eventUUID, eventSlug: payload.slug }
  // });
  /*
  const credentials = yield select(getCredentials);
  let convertSessionPayload = {};

  try {
    convertSessionPayload = yield call(Api.convertSession, credentials, {
      eventUUID: payload.eventUUID,
      sessionId: payload.sessionId,
      userId: credentials.userId
    });
  } catch (e) {
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred converting session"
        }
      ])
    );
  } finally {
    if (
      !convertSessionPayload.payload ||
      convertSessionPayload.payload.expired
    ) {
      window.location = `/applications/${payload.slug}/${payload.eventUUID}`;
      return true;
    }

    window.location = `/portals/${convertSessionPayload.payload.event.slug}/${convertSessionPayload.payload.event.uuid}`;
    return true;
  }
  */
};

const handleLogin = function*({ payload }) {
  if (payload.err) {
    // eslint-disable-next-line no-console
    console.error(
      `[Applications Landing] Error logging in`,
      payload.err.response.body.message
    );
    yield put(actions.setSearching(false));
    yield put(actions.setError(payload.err.response.body.message));
  } else {
    yield put(actions.setSearching(true));
  }
};

const saveValues = function*() {
  const { eventId, eventUUID, intakeId } = yield call(getParams);
  const sessionId = yield select(getters.sessionId);
  const submissionId = yield select(getters.submissionId);
  const contactValues = yield select(getContactValues);
  const contactFields = yield select(getContactDataFields);
  const formData = yield select(getters.formData);
  const credentials = yield select(getCredentials);

  yield put(actions.setError(""));
  yield put(actions.setSaving(true));

  // create submission
  try {
    yield call(Api.createSubmission, credentials, intakeId, {
      sessionId,
      eventUUID,
      eventId,
      submissionId,
      contactValues: R.pick(contactFields.map(f => f.id))(contactValues)
    });

    window.location = `/portals/${formData.event_slug}/${eventUUID}`;
  } catch (e) {
    yield put(actions.setSaving(false));
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred saving session"
        }
      ])
    );
  }
};

const onCreateAccount = function*() {
  const { eventId } = yield call(getParams);
  const userExists = yield select(getters.userExists);
  const isVirtualUser = yield select(getters.isVirtualUser);
  const portalUserExists = yield select(getters.portalUserExists);
  const formData = yield select(getters.formData);
  const credentials = yield select(getCredentials);
  const loginValues = yield select(getters.loginValues);
  let email = R.pathOr("", ["email", "value"])(loginValues);
  email = email.toLowerCase().trim();
  const password = R.pathOr("", ["password", "value"])(loginValues);

  yield put(actions.setError(null));
  yield put(actions.setSearching(true));

  try {
    if (userExists === false) {
      // handle: user does not exist
      yield call(UserApi.post, credentials, {
        email,
        password,
        firstName: "",
        lastName: "",
        eventId,
        contactTypeId: formData.contact_record_type_id,
        createContactAsDraft: false
      });
    } else if (
      userExists === true &&
      isVirtualUser === false &&
      portalUserExists === false
    ) {
      // handle: user exists, is not a virtual user, is not a portal user
    } else if (userExists === true && isVirtualUser === true) {
      // handle: user exists, is a virtual user, is a portal user
      yield call(UserApi.convert, credentials, {
        email,
        password
      });
    }

    window.webAuth.login(
      {
        username: email,
        password,
        realm: window.__AUTH0_REALM__,
        redirectUri: `${window.__LENND_APP_URL__}/applications-callback?eventUUID=${formData.form_slug}&intakeFormId=${formData.form_id}&slug=${formData.form_slug}`
      },
      err =>
        loginChannel.put({
          type: actions.handleLogin.type,
          payload: {
            err
          }
        })
    );
  } catch (e) {
    yield put(actions.setSearching(false));
    yield put(actions.setError(e.message));
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred creating account"
        }
      ])
    );
  }
};

const onLogin = function*() {
  yield put(actions.setError(null));
  yield put(actions.setSearching(true));

  const formData = yield select(getters.formData);
  const loginValues = yield select(getters.loginValues);
  let email = R.pathOr("", ["email", "value"])(loginValues);
  email = email.toLowerCase().trim();
  const password = R.pathOr("", ["password", "value"])(loginValues);

  try {
    window.webAuth.login(
      {
        username: email,
        password,
        realm: window.__AUTH0_REALM__,
        redirectUri: `${window.__LENND_APP_URL__}/applications-callback?eventUUID=${formData.form_id}&intakeFormId=${formData.form_id}&slug=${formData.form_slug}`
      },
      err =>
        loginChannel.put({
          type: actions.handleLogin.type,
          payload: {
            err
          }
        })
    );
  } catch (e) {
    yield put(actions.setSearching(false));
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred while logging in"
        }
      ])
    );
  }
};

const nextPage = function*() {
  const activeIndex = yield select(getters.activeIndex);

  if (activeIndex < 2) {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    yield put(actions.setActiveIndex(activeIndex + 1));
  } else {
    yield call(saveValues);
  }
};

const backPage = function*() {
  const activeIndex = yield select(getters.activeIndex);

  if (activeIndex > 0) {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
    yield put(actions.setActiveIndex(activeIndex - 1));
  }
};

const goToLoginPage = function*() {
  const formData = yield select(getters.formData);
  window.location = `/portal-login/${formData.event_slug}/${formData.event_uuid}?returnTo=/applications/${formData.form_slug}/${formData.form_id}`;
};

const handleLogout = function*() {
  const formData = yield select(getters.formData);
  window.location = `/logout?returnTo=/applications/${formData.form_slug}/${formData.form_id}`;
};

const onEmailUpdate = function*() {
  const loginValues = yield select(getters.loginValues);
  const emailValue = loginValues.email;
  const email = emailValue ? emailValue.value.trim().toLowerCase() : null;

  const { eventId } = yield call(getParams);
  const credentials = yield select(getCredentials);

  yield put(
    actions.updateLoginValues({
      id: "password",
      value: {
        type: "password",
        value: null
      }
    })
  );

  if (!email || !Helpers.isValidEmail(email)) {
    yield put(
      actions.setUserData({
        emailValidated: email,
        shouldEnforceSso: false,
        userExists: false,
        isVirtualUser: false,
        portalUserExists: false,
        userValidated: false,
        isPasswordValid: false,
        error: "Please enter a valid email address"
      })
    );
    return false;
  }

  yield put(actions.setSearching(true));

  const {
    payload: { userExists, isVirtualUser, portalUserExists, shouldEnforceSso }
  } = yield call(PortalUsersApi.searchUsers, credentials, {
    eventId,
    email
  });

  yield put(actions.setSearching(false));
  yield put(
    actions.setUserData({
      emailValidated: email,
      userExists,
      isVirtualUser,
      portalUserExists,
      shouldEnforceSso,
      userValidated: true,
      isPasswordValid: false,
      error: null
    })
  );

  return true;
};

const onLoginValuesUpdate = function*({ payload: { id, value } }) {
  if (id === "email") {
    const emailValidated = yield select(getters.emailValidated);
    const userValidated = yield select(getters.userValidated);
    const email = value ? value.value.trim().toLowerCase() : null;

    if (userValidated && emailValidated && emailValidated !== email) {
      yield put(
        actions.setUserData({
          emailValidated: null,
          userExists: false,
          isVirtualUser: false,
          portalUserExists: false,
          userValidated: false,
          isPasswordValid: false,
          shouldEnforceSso: false,
          error: null
        })
      );
    }
  }
};

const onTabChange = function*({ payload: tabId }) {
  if (tabId === "add") {
    yield put(actions.addSpeaker());
  }
  yield put(actions.setSelectedSpeakerId(tabId));

  return true;
};

const onAddSpeaker = function*() {
  const speakers = yield select(getters.speakers);
  yield put(TabActions.setSelectedTab(speakers[speakers.length - 1].id));
  return true;
};

const onRemoveSpeaker = function*() {
  const selectedSpeakerId = yield select(getters.selectedSpeakerId);
  yield put(TabActions.setSelectedTab(selectedSpeakerId));
  return true;
};

const fetchSingleSignOnIdentityProviders = function*() {
  const formData = yield select(getters.formData);
  const { payload } = yield call(SSOApi.applicationFormsList, {
    formId: formData.form_id
  });
  yield put(actions.setSingleSignOnIdentityProviders(payload));
};

const watchFetchSingleSignOnIdentityProviders = function*() {
  yield takeEvery(
    actions.fetchSingleSignOnIdentityProviders.type,
    fetchSingleSignOnIdentityProviders
  );
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchCallback = function*() {
  yield takeEvery(actions.handleCallback.type, handleCallback);
};

const watchLoginCallback = function*() {
  yield takeEvery(actions.handleLogin.type, handleLogin);
};

const watchLogout = function*() {
  yield takeEvery(actions.handleLogout.type, handleLogout);
};

const watchLoginChannel = function*() {
  while (true) {
    const action = yield take(loginChannel);
    yield put(action);
  }
};

const watchNextPage = function*() {
  yield takeEvery(actions.nextPage.type, nextPage);
};

const watchBackPage = function*() {
  yield takeEvery(actions.backPage.type, backPage);
};

const watchGoToLoginPage = function*() {
  yield takeEvery(actions.goToLoginPage.type, goToLoginPage);
};

const watchEmailUpdate = function*() {
  yield takeEvery(
    [actions.validateLogin.type, actions.checkUser.type],
    onEmailUpdate
  );
};

const watchLoginValuesUpdate = function*() {
  yield takeEvery(actions.updateLoginValues.type, onLoginValuesUpdate);
};

const watchChangeTab = function*() {
  yield takeEvery(TabActions.setSelectedTab.type, onTabChange);
};

const watchAddSpeaker = function*() {
  yield takeEvery(actions.addSpeaker.type, onAddSpeaker);
};

const watchRemoveSpeaker = function*() {
  yield takeEvery(actions.removeSpeaker.type, onRemoveSpeaker);
};

const watchOnLogin = function*() {
  yield takeEvery(actions.onLogin.type, onLogin);
};

const watchOnCreateAccount = function*() {
  yield takeEvery(actions.onCreateAccount.type, onCreateAccount);
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchCallback),
    fork(watchNextPage),
    fork(watchGoToLoginPage),
    fork(watchEmailUpdate),
    fork(watchLoginValuesUpdate),
    fork(watchLogout),
    fork(watchLoginChannel),
    fork(watchLoginCallback),
    fork(watchBackPage),
    fork(watchChangeTab),
    fork(watchAddSpeaker),
    fork(watchRemoveSpeaker),
    fork(watchOnLogin),
    fork(watchOnCreateAccount),
    fork(watchFetchSingleSignOnIdentityProviders)
  ]);
};

export default rootSaga;
